import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Popover } from '~components/Popover';
import { P } from '~components/Typography';
import { useAccountContext } from '~context/account';
import { useBotPaywallContext } from '~context/botPaywall';
import { usePaywallContext } from '~context/paywall';
import { CheckoutReason } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { dialogsApi } from '~services/api/dialogs';
import { eventsInbox, EventType } from '~shared/services/eventsInbox';
import { transformMessagePaymentType } from '~utils/payments';

import RefreshIcon from '../../../../../public/assets/icons/refresh.svg';
import type { TMessageMetaTariffication } from '../../model/types';
import {
	hiddenInTouchscreen,
	LoadingStyle,
	RegenerateButtonInner,
	RegenerateButtonWrapper,
} from './styles';

export type RegenerateProps = {
	messageId: number;
	userId: string;
	recipientId: string;
	regeneration: Required<
		Pick<TMessageMetaTariffication, 'regeneration'>
	>['regeneration'];
	isMessageFromInfluencer: boolean;
};
export const RegenerateButton: FC<RegenerateProps> = ({
	messageId,
	userId,
	recipientId,
	regeneration,
	isMessageFromInfluencer,
}) => {
	const [send, { isLoading, isError }] =
		dialogsApi.useRegenerateMessageMutation();
	const [loading, setLoading] = useState(false);
	const ref = useRef<HTMLDivElement | null>(null);
	const { openPaywall } = usePaywallContext();
	const { balance, hasSubscription } = useAccountContext();
	const { openBotPaywall } = useBotPaywallContext();
	const sendAnnalsClicked = annals.chat.useRegenerationClicked();

	const { cost, type } = regeneration;
	const regenerationType = transformMessagePaymentType({
		type,
		cost,
		isPaid: false,
		isOwnMessage: false,
	});

	const subscribeAndBuy = useCallback(
		(isSuccess?: boolean) => {
			if (!isSuccess || loading || isLoading) {
				return;
			}

			const unsub = eventsInbox.onTypeMsg(
				EventType.BalanceChanged,
				(payload) => {
					// ignore decrease balance events
					if (payload.change <= 0) {
						return;
					}

					unsub();

					if (payload.balance < cost) {
						return;
					}

					// TODO: process send error?
					setLoading(true);
					void send({ messageId, userId, recipientId });
				}
			);
		},
		[isLoading, loading, messageId, recipientId, cost, send, userId]
	);

	const onClick = () => {
		void sendAnnalsClicked();

		const regenerate = () => {
			setLoading(true);
			void send({ messageId, userId, recipientId });
		};

		if (regenerationType.includes('subscription') && !hasSubscription) {
			if (isMessageFromInfluencer) {
				return openBotPaywall({
					checkoutReason: CheckoutReason.Regeneration,
					callback: subscribeAndBuy,
				});
			} else {
				return openPaywall({
					prefer: 'subscription',
					checkoutReason: CheckoutReason.Regeneration,
					botClientId: recipientId,
					callback: subscribeAndBuy,
				})();
			}
		}
		if (regenerationType.includes('subscription-bot') && !hasSubscription) {
			return openBotPaywall({
				checkoutReason: CheckoutReason.Regeneration,
				callback: subscribeAndBuy,
			});
		}

		if (regenerationType.includes('purchase')) {
			if (balance < cost) {
				return openPaywall({
					checkoutReason: CheckoutReason.Regeneration,
					botClientId: recipientId,
					callback: subscribeAndBuy,
				})();
			}
			return regenerate();
		}
	};

	useEffect(() => {
		setLoading(false);
	}, [messageId, isError]);

	return (
		<>
			<RegenerateButtonWrapper
				ref={ref}
				tabIndex={-1}
				id={`${messageId}-popover`}
				onClick={isLoading || loading ? undefined : onClick}
			>
				<RegenerateButtonInner
					className={isLoading || loading ? LoadingStyle : ''}
				>
					<RefreshIcon />
				</RegenerateButtonInner>
			</RegenerateButtonWrapper>
			<Popover ref={ref} className={hiddenInTouchscreen}>
				<P size="xsmall" align="center">
					Regenerate
				</P>
				<P size="xsmall" align="center" transparent>
					{regeneration.cost} neurons
				</P>
			</Popover>
		</>
	);
};
